const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Formats a date for display as "Aug 5, 2026".
 * Accepts Date, ISO/datetime strings, or YYYY-MM-DD. Returns fallback for empty/invalid.
 */
export const formatDisplayDate = (value, fallback = "—") => {
  if (value == null || value === "") return fallback;

  let year;
  let month;
  let day;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return fallback;
    year = value.getFullYear();
    month = value.getMonth() + 1;
    day = value.getDate();
  } else {
    const text = String(value).trim();
    const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
      year = Number(dateOnly[1]);
      month = Number(dateOnly[2]);
      day = Number(dateOnly[3]);
    } else {
      const parsed = new Date(text);
      if (Number.isNaN(parsed.getTime())) return fallback;
      year = parsed.getFullYear();
      month = parsed.getMonth() + 1;
      day = parsed.getDate();
    }
  }

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return fallback;
  }

  return `${MONTH_SHORT[month - 1]} ${day}, ${year}`;
};

function parseLocalDateParts(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }

  const text = String(value).trim();
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return {
      year: Number(dateOnly[1]),
      month: Number(dateOnly[2]),
      day: Number(dateOnly[3]),
    };
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
  };
}

function toStartOfLocalDay(parts) {
  return new Date(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0);
}

/**
 * Student-facing due-date copy. Tone hardens as the date passes.
 * Returns pill label, next-step label, and visual tone.
 */
export function getFeeDueMessaging(duedate, now = new Date()) {
  const formatted = formatDisplayDate(duedate, null);
  const parts = parseLocalDateParts(duedate);

  if (!formatted || !parts) {
    return {
      urgency: "unknown",
      pillLabel: "Due date TBA",
      nextStepLabel: "Next step",
      tone: "neutral",
      daysFromDue: null,
    };
  }

  const todayParts = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
  const msInDay = 1000 * 60 * 60 * 24;
  const daysFromDue = Math.round(
    (toStartOfLocalDay(parts) - toStartOfLocalDay(todayParts)) / msInDay,
  );

  if (daysFromDue > 7) {
    return {
      urgency: "upcoming",
      pillLabel: `Pay by ${formatted}`,
      nextStepLabel: "Next step",
      tone: "calm",
      daysFromDue,
    };
  }

  if (daysFromDue > 1) {
    return {
      urgency: "due_soon",
      pillLabel: `Due in ${daysFromDue} days · ${formatted}`,
      nextStepLabel: "Next step",
      tone: "warn",
      daysFromDue,
    };
  }

  if (daysFromDue === 1) {
    return {
      urgency: "due_tomorrow",
      pillLabel: `Due tomorrow · ${formatted}`,
      nextStepLabel: "Next step",
      tone: "warn",
      daysFromDue,
    };
  }

  if (daysFromDue === 0) {
    return {
      urgency: "due_today",
      pillLabel: "Due today — pay before end of day",
      nextStepLabel: "Pay today",
      tone: "urgent",
      daysFromDue,
    };
  }

  const daysOverdue = Math.abs(daysFromDue);

  if (daysOverdue === 1) {
    return {
      urgency: "overdue",
      pillLabel: `1 day overdue · was ${formatted}`,
      nextStepLabel: "Payment overdue",
      tone: "urgent",
      daysFromDue,
    };
  }

  if (daysOverdue <= 7) {
    return {
      urgency: "overdue",
      pillLabel: `${daysOverdue} days overdue · was ${formatted}`,
      nextStepLabel: "Payment overdue",
      tone: "urgent",
      daysFromDue,
    };
  }

  if (daysOverdue <= 30) {
    return {
      urgency: "overdue_long",
      pillLabel: `Overdue since ${formatted}`,
      nextStepLabel: "Pay now",
      tone: "critical",
      daysFromDue,
    };
  }

  const monthsOverdue = Math.floor(daysOverdue / 30);
  return {
    urgency: "overdue_long",
    pillLabel: `Long overdue · ${monthsOverdue}+ month${
      monthsOverdue === 1 ? "" : "s"
    } past due`,
    nextStepLabel: "Pay immediately",
    tone: "critical",
    daysFromDue,
  };
}

/**
 * Timer + disclaimer for the LMS fee-warning gate.
 * Duration hardens as the due date falls further behind.
 */
export function getFeeWarningSchedule(duedate, now = new Date()) {
  const formatted = formatDisplayDate(duedate, null);
  const parts = parseLocalDateParts(duedate);

  if (!formatted || !parts) {
    return {
      durationSec: 5,
      message:
        "Please pay your fees before the due date to avoid any inconvenience.",
    };
  }

  const todayParts = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };
  const msInDay = 1000 * 60 * 60 * 24;
  const daysFromDue = Math.round(
    (toStartOfLocalDay(parts) - toStartOfLocalDay(todayParts)) / msInDay,
  );

  if (daysFromDue > 0) {
    return {
      durationSec: 5,
      message: `Your fee is due on ${formatted}. Pay it before the due date to avoid any inconvenience.`,
    };
  }

  if (daysFromDue === 0) {
    return {
      durationSec: 30,
      message:
        "Your fee is due today. Pay it before the day end to avoid any inconvenience. You cannot skip this message — use this time to arrange payment.",
    };
  }

  if (daysFromDue === -1) {
    return {
      durationSec: 60,
      message:
        "Your fee was due yesterday. Pay it by tonight or your access may stay restricted. You cannot skip this message — use this time to arrange payment.",
    };
  }

  return {
    durationSec: 3600 * 24 * 7,
    message: `Your fee was due on ${formatted}. Access for this course stays restricted until you pay. You cannot skip this message — use this time to arrange payment.`,
  };
}
